"use client"

import PageHeader from "@/components/layout/PageHeader";
import { useState, useEffect, useCallback } from 'react'
import { motion } from 'framer-motion'
import {
  User,
  Shield,
  Database,
  RefreshCw,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Globe,
  Monitor,
  LogIn,
  LogOut,
  Edit,
  Trash2,
  Plus,
  Clock,
} from 'lucide-react'
import { get } from '../utils/service'
import Drawer from '@/components/ui/Drawer'

interface AuditLog {
  id: string
  action: string
  table_name: string
  record_id: string
  user_id: string
  user_name: string
  description: string
  ip_address: string
  user_agent: string
  created_at: string
  record_details?: Record<string, unknown>
  old_values_parsed?: Record<string, unknown>
  new_values_parsed?: Record<string, unknown>
}

interface PaginationState {
  page: number
  per_page: number
  total: number
  total_pages: number
}

interface FilterState {
  action: string
  table_name: string
  date_from: string
  date_to: string
}

interface AuditLogsResponse {
  success: boolean
  message: string
  data: {
    data: AuditLog[]
    total: number
    total_pages: number
    current_page: number
    per_page: number
  }
}

const getActionIcon = (action: string) => {
  if (!action) return <Database className="w-3.5 h-3.5" />
  if (action.includes('CREATE')) return <Plus className="w-3.5 h-3.5" />
  if (action.includes('UPDATE')) return <Edit className="w-3.5 h-3.5" />
  if (action.includes('DELETE')) return <Trash2 className="w-3.5 h-3.5" />
  if (action.includes('LOGIN')) return <LogIn className="w-3.5 h-3.5" />
  if (action.includes('LOGOUT')) return <LogOut className="w-3.5 h-3.5" />
  if (action.includes('APPROVE')) return <CheckCircle className="w-3.5 h-3.5" />
  if (action.includes('ASSIGN')) return <User className="w-3.5 h-3.5" />
  return <Database className="w-3.5 h-3.5" />
}

const getActionColors = (action: string) => {
  if (!action) return { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400', line: 'border-gray-200', badge: 'bg-gray-100 text-gray-600' }
  if (action.includes('CREATE')) return { bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500', line: 'border-green-200', badge: 'bg-green-100 text-green-700' }
  if (action.includes('UPDATE')) return { bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500', line: 'border-blue-200', badge: 'bg-blue-100 text-blue-700' }
  if (action.includes('DELETE')) return { bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500', line: 'border-red-200', badge: 'bg-red-100 text-red-700' }
  if (action.includes('LOGIN')) return { bg: 'bg-purple-100', text: 'text-purple-700', dot: 'bg-purple-500', line: 'border-purple-200', badge: 'bg-purple-100 text-purple-700' }
  if (action.includes('LOGOUT')) return { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400', line: 'border-gray-200', badge: 'bg-gray-100 text-gray-600' }
  if (action.includes('APPROVE')) return { bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500', line: 'border-emerald-200', badge: 'bg-emerald-100 text-emerald-700' }
  if (action.includes('ASSIGN')) return { bg: 'bg-orange-100', text: 'text-orange-700', dot: 'bg-orange-500', line: 'border-orange-200', badge: 'bg-orange-100 text-orange-700' }
  return { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400', line: 'border-gray-200', badge: 'bg-gray-100 text-gray-600' }
}

const formatDate = (dateString: string) => {
  if (!dateString) return 'Unknown date'
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return 'Invalid date'
  }
}

const formatTime = (dateString: string) => {
  if (!dateString) return ''
  try {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return ''
  }
}

const formatDateLabel = (dateString: string) => {
  if (!dateString) return 'Unknown'
  try {
    const date = new Date(dateString)
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(today.getDate() - 1)

    if (date.toDateString() === today.toDateString()) return 'Today'
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday'
    return date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
  } catch {
    return 'Unknown'
  }
}

const groupLogsByDate = (logs: AuditLog[]) => {
  const groups: Record<string, AuditLog[]> = {}
  logs.forEach((log) => {
    if (!log?.created_at) return
    const dateKey = new Date(log.created_at).toDateString()
    if (!groups[dateKey]) groups[dateKey] = []
    groups[dateKey].push(log)
  })
  return groups
}

const formatActionText = (action: string) => {
  if (!action) return 'unknown action'
  return action.toLowerCase().replace(/_/g, ' ')
}

const getUserDisplayName = (log: AuditLog) => {
  if (log.user_name) return log.user_name
  if (log.record_details && typeof log.record_details === 'object') {
    const details = log.record_details as { first_name?: string; last_name?: string }
    if (details.first_name && details.last_name) return `${details.first_name} ${details.last_name}`
  }
  return 'System'
}

const getRecordDescription = (log: AuditLog) => {
  if (!log) return 'System action'
  if (log.description) return log.description
  if (log.table_name && log.record_id) return `${log.table_name} record ${log.record_id}`
  return 'System action'
}

const AuditLogs = () => {
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery] = useState('')
  const [filters] = useState<FilterState>({
    action: '',
    table_name: '',
    date_from: '',
    date_to: '',
  })
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    per_page: 50,
    total: 0,
    total_pages: 0,
  })
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const fetchAuditLogs = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({
        page: String(pagination.page),
        per_page: String(pagination.per_page),
        ...filters,
      }).toString()

      const response = await get<AuditLogsResponse>(`/audit/logs?${params}`)

      if (response && response.success && response.data) {
        const data = response.data.data || []
        const total = Math.floor(response.data.total) || 0
        const totalPages = response.data.total_pages || 1

        setAuditLogs(Array.isArray(data) ? data : [])
        setPagination((prev) => ({ ...prev, total, total_pages: totalPages }))
      } else {
        setAuditLogs([])
        setPagination((prev) => ({ ...prev, total: 0, total_pages: 0 }))
      }
    } catch (error) {
      console.error('Error fetching audit logs:', error)
      setAuditLogs([])
      setPagination((prev) => ({ ...prev, total: 0, total_pages: 0 }))
    } finally {
      setLoading(false)
    }
  }, [pagination.page, pagination.per_page, filters])

  useEffect(() => {
    fetchAuditLogs()
  }, [fetchAuditLogs])

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleViewDetails = (log: AuditLog) => {
    setSelectedLog(log)
    setDrawerOpen(true)
  }

  const filteredLogs = auditLogs.filter((log) => {
    if (!searchQuery) return true
    const q = searchQuery.toLowerCase()
    return (
      log.description?.toLowerCase().includes(q) ||
      log.user_name?.toLowerCase().includes(q) ||
      log.action?.toLowerCase().includes(q) ||
      log.table_name?.toLowerCase().includes(q) ||
      log.ip_address?.toLowerCase().includes(q)
    )
  })

  const groupedLogs = groupLogsByDate(filteredLogs)
  const dateKeys = Object.keys(groupedLogs)

  return (
    <div className="min-h-screen p-4 sm:p-0">
      <div className="mx-auto">

        <PageHeader title="Audit Logs" description="Monitor all system activities and user actions">
          <button
              type="button"
              onClick={fetchAuditLogs}
              className="flex items-center gap-2 px-4 py-1.5 bg-gradient-to-r from-primary to-primary/90 text-white text-sm font-light tracking-wide rounded-full hover:from-black/90 hover:to-black transition-all duration-200"
            >
              <RefreshCw className="w-3 h-3" />
              Refresh
            </button>
        </PageHeader>

        {/* Timeline Feed */}
        <div className="space-y-0">
          {loading ? (
            <div className="space-y-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="flex gap-4 animate-pulse">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-gray-200" />
                    {i < 7 && <div className="w-0.5 h-12 bg-gray-100 mt-1" />}
                  </div>
                  <div className="flex-1 pb-6">
                    <div className="h-3 bg-gray-200 rounded w-1/4 mb-2" />
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-gray-100 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-7 h-7 text-gray-300" />
              </div>
              <h3 className="text-base font-light text-gray-900 mb-1">No audit logs found</h3>
              <p className="text-sm font-light text-gray-500">No system activities match your current filters.</p>
            </div>
          ) : (
            dateKeys.map((dateKey) => {
              const logs = groupedLogs[dateKey]
              const label = formatDateLabel(logs[0]?.created_at)
              return (
                <div key={dateKey} className="mb-8">
                  {/* Date separator */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex items-center gap-2">
                      <Clock className="w-3 h-3 text-gray-400" />
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-widest">{label}</span>
                    </div>
                    <div className="flex-1 h-px bg-gray-100" />
                    <span className="text-xs text-gray-400">{logs.length} event{logs.length !== 1 ? 's' : ''}</span>
                  </div>

                  {/* Log entries */}
                  <div className="relative">
                    {/* Vertical timeline line */}
                    <div className="absolute left-4 top-0 bottom-0 w-px bg-gray-100" />

                    <div className="space-y-1">
                      {logs.map((log, idx) => {
                        const colors = getActionColors(log?.action)
                        const isLast = idx === logs.length - 1
                        return (
                          <motion.div
                            key={log?.id || idx}
                            initial={{ opacity: 0, x: -8 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: idx * 0.03 }}
                            className="relative flex gap-4"
                          >
                            {/* Timeline dot */}
                            <div className="relative flex-shrink-0 flex flex-col items-center w-8">
                              <div className={`relative z-10 w-8 h-8 rounded-full ${colors.bg} ${colors.text} flex items-center justify-center ring-2 ring-white`}>
                                {getActionIcon(log?.action)}
                              </div>
                              {!isLast && <div className="w-px flex-1 bg-gray-100 mt-1" />}
                            </div>

                            {/* Content card */}
                            <div
                              className={`flex-1 mb-${isLast ? '0' : '2'} pb-4 cursor-pointer group`}
                              onClick={() => handleViewDetails(log)}
                            >
                              <div className="bg-white border border-gray-100 rounded-xl px-4 py-3 hover:border-primary/30 hover:shadow-sm transition-all duration-200 group-hover:bg-gray-50/50">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="flex-1 min-w-0">
                                    {/* Action badge + table */}
                                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${colors.badge}`}>
                                        {getActionIcon(log?.action)}
                                        {formatActionText(log?.action)}
                                      </span>
                                      {log?.table_name && (
                                        <span className="text-xs text-gray-400 font-mono">
                                          {log.table_name}
                                        </span>
                                      )}
                                    </div>

                                    {/* Description */}
                                    <p className="text-sm text-gray-800 font-light leading-snug line-clamp-2">
                                      {getRecordDescription(log)}
                                    </p>

                                    {/* Meta row */}
                                    <div className="flex items-center gap-3 mt-2 flex-wrap">
                                      <div className="flex items-center gap-1.5">
                                        <div className="w-4 h-4 rounded-full bg-gray-200 flex items-center justify-center">
                                          <User className="w-2.5 h-2.5 text-gray-500" />
                                        </div>
                                        <span className="text-xs text-gray-500">{getUserDisplayName(log)}</span>
                                      </div>
                                      {log?.ip_address && (
                                        <div className="flex items-center gap-1">
                                          <Globe className="w-3 h-3 text-gray-300" />
                                          <span className="text-xs text-gray-400 font-mono">{log.ip_address}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {/* Timestamp */}
                                  <div className="flex-shrink-0 text-right">
                                    <span className="text-xs text-gray-400 whitespace-nowrap">{formatTime(log?.created_at)}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        )
                      })}
                    </div>
                  </div>
                </div>
              )
            })
          )}
        </div>

        {/* Pagination */}
        {!loading && filteredLogs.length > 0 && pagination.total_pages > 1 && (
          <div className="flex items-center justify-between px-4 py-4 border-t border-gray-100 mt-4">
            <p className="text-xs text-gray-500">
              Showing {((pagination.page - 1) * pagination.per_page) + 1}–
              {Math.min(pagination.page * pagination.per_page, pagination.total)} of {pagination.total} events
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                aria-label="Previous page"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="p-1.5 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
              >
                <ChevronLeft className="w-3 h-3" />
              </button>
              <span className="px-3 py-1 text-xs text-gray-600 bg-gray-50 rounded-lg border border-gray-200">
                {pagination.page} / {pagination.total_pages}
              </span>
              <button
                type="button"
                aria-label="Next page"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.total_pages}
                className="p-1.5 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
              >
                <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Drawer */}
      <Drawer
        isOpen={drawerOpen}
        onClose={() => { setDrawerOpen(false); setSelectedLog(null) }}
        position="right"
        title="Audit Log Details"
        showCloseButton
      >
        {selectedLog && (
          <div className="h-full overflow-y-auto p-4 space-y-5">
            {/* Action header */}
            <div className={`flex items-center gap-3 p-3 rounded-xl ${getActionColors(selectedLog.action).bg}`}>
              <div className={`w-9 h-9 rounded-full bg-white/70 flex items-center justify-center ${getActionColors(selectedLog.action).text}`}>
                {getActionIcon(selectedLog.action)}
              </div>
              <div>
                <span className={`text-xs font-semibold uppercase tracking-wide ${getActionColors(selectedLog.action).text}`}>
                  {formatActionText(selectedLog.action)}
                </span>
                <p className="text-xs text-gray-500 mt-0.5">{formatDate(selectedLog.created_at)}</p>
              </div>
            </div>

            {/* Description */}
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Description</p>
              <p className="text-sm text-gray-100 bg-gray-800 rounded-lg p-3 leading-relaxed">
                {getRecordDescription(selectedLog)}
              </p>
            </div>

            {/* Details grid */}
            <div className="space-y-3">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Details</p>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-800 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">User</p>
                  <p className="text-xs text-gray-100 font-medium">{getUserDisplayName(selectedLog)}</p>
                </div>
                <div className="bg-gray-800 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Table</p>
                  <p className="text-xs text-gray-100 font-medium font-mono">{selectedLog.table_name || '—'}</p>
                </div>
                <div className="bg-gray-800 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">IP Address</p>
                  <div className="flex items-center gap-1.5">
                    <Globe className="w-3 h-3 text-gray-500" />
                    <p className="text-xs text-gray-100 font-mono">{selectedLog.ip_address || '—'}</p>
                  </div>
                </div>
                <div className="bg-gray-800 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Record ID</p>
                  <p className="text-xs text-gray-100 font-mono truncate" title={selectedLog.record_id}>{selectedLog.record_id || '—'}</p>
                </div>
              </div>
            </div>

            {/* User Agent */}
            {selectedLog.user_agent && (
              <div>
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Device</p>
                <div className="flex items-start gap-2 bg-gray-800 p-3 rounded-lg">
                  <Monitor className="w-3 h-3 text-gray-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-gray-300 font-mono break-all leading-relaxed">{selectedLog.user_agent}</p>
                </div>
              </div>
            )}

            {/* Old Values */}
            {selectedLog.old_values_parsed && (
              <div>
                <p className="text-xs font-medium text-red-400 uppercase tracking-wider mb-2">Before</p>
                <pre className="text-xs text-gray-300 bg-red-950/40 border border-red-900/30 p-3 rounded-lg overflow-x-auto leading-relaxed">
                  {JSON.stringify(selectedLog.old_values_parsed, null, 2)}
                </pre>
              </div>
            )}

            {/* New Values */}
            {selectedLog.new_values_parsed && (
              <div>
                <p className="text-xs font-medium text-green-400 uppercase tracking-wider mb-2">After</p>
                <pre className="text-xs text-gray-300 bg-green-950/40 border border-green-900/30 p-3 rounded-lg overflow-x-auto leading-relaxed">
                  {JSON.stringify(selectedLog.new_values_parsed, null, 2)}
                </pre>
              </div>
            )}

            {/* Record Details */}
            {selectedLog.record_details && (
              <div>
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Record Data</p>
                <pre className="text-xs text-gray-300 bg-gray-800 p-3 rounded-lg overflow-x-auto leading-relaxed">
                  {JSON.stringify(selectedLog.record_details, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  )
}

export default AuditLogs
